import React, {
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import {
  withStripes,
} from '@folio/stripes/core';
import {
  Col,
  Row,
  Loading,
} from '@folio/stripes/components';

import Condition from '../form-condition';
import RemoveUserAssignment from '../remove-user-assignment';
import SelectAssignment from '../select-assignment';
import SelectTeam from '../select-team';
import SelectTeamMember from '../select-team-member';
import SelectUserLookUp from '../select-user-look-up';

import {
  ASSIGN_TO,
  ASSIGNED_USER_NAME,
  ASSIGNED_USER_ID,
  TEAM,
  ASSIGNED_USER_ID_INITIAL_VALUE,
  ASSIGNED_USER_NAME_INITIAL_VALUE,
  ASSIGN_TO_INITIAL_VALUE,
  TEAM_INITIAL_VALUE,
  TASK_ASSIGN_TO_FIELDS,
  TEAM_FIELDS,
} from '../../../constants';

import css from './assignment.css';

const Assignment = ({
  index,
  fields,
  task,
  stripes,
  teams,
  getTeams,
  teamsLoaded,
  users,
  getUsers,
}) => {
  const teamId = fields.value[index][TEAM]?.[TEAM_FIELDS.ID];
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);

  useEffect(
    () => {
      let isSubscribed = true;

      if (teamId && teams.length) {
        getUsers(teamId)
          .then(() => {
            if (isSubscribed) {
              setIsLoaderVisible(false);
            }
          });
      }

      if (!teamId) {
        setIsLoaderVisible(false);
      }

      return () => {
        isSubscribed = false;
      };
    },
    [teamId, teams.length],
  );

  const change = (value) => {
    fields.update(index, {
      ...fields.value[index],
      ...value,
    });
  };

  const isTeamMembersLoaded = (teamMembers) => Array.isArray(teamMembers);

  const removeUserAssignment = () => {
    fields.update(index, {
      ...fields.value[index],
      ...{
        [ASSIGN_TO]: ASSIGN_TO_INITIAL_VALUE,
        [TEAM]: TEAM_INITIAL_VALUE,
        [ASSIGNED_USER_ID]: ASSIGNED_USER_ID_INITIAL_VALUE,
        [ASSIGNED_USER_NAME]: ASSIGNED_USER_NAME_INITIAL_VALUE,
      },
    });
  };

  if (teamsLoaded && !teams.length) {
    return (
      <Row>
        <SelectUserLookUp
          isObjectDataFormat
          noTeams
          id={`${ASSIGNED_USER_NAME}${index}`}
          name={`${task}.${ASSIGNED_USER_NAME}`}
          stripes={stripes}
          change={change}
        />
      </Row>
    );
  }

  if (isLoaderVisible) {
    return (
      <Row
        data-test-assignment-loader
        className={css.isLoadingRow}
        middle="sm"
      >
        <Col sm={6}>
          <Loading />
        </Col>
      </Row>
    );
  }

  return (
    <Row data-test-assignment-row>
      <Col sm={9}>
        <Row>
          <SelectAssignment
            isObjectDataFormat
            id={`${ASSIGN_TO}${index}`}
            name={`${task}.${ASSIGN_TO}`}
            change={change}
            getTeams={getTeams}
          />

          <Condition
            is={TASK_ASSIGN_TO_FIELDS.USER}
            when={fields.value[index][ASSIGN_TO]}
          >
            <SelectUserLookUp
              isObjectDataFormat
              id={`${ASSIGNED_USER_NAME}${index}`}
              name={`${task}.${ASSIGNED_USER_NAME}`}
              stripes={stripes}
              change={change}
            />
          </Condition>

          <Condition
            is={TASK_ASSIGN_TO_FIELDS.TEAM}
            when={fields.value[index][ASSIGN_TO]}
          >
            <SelectTeam
              isObjectDataFormat
              id={`${TEAM}.${TEAM_FIELDS.ID}${index}`}
              name={`${task}.${TEAM}.${TEAM_FIELDS.ID}`}
              teams={teams}
              change={change}
              getUsers={getUsers}
            />
          </Condition>

          { fields.value[index][TEAM]?.[TEAM_FIELDS.ID] && (
            <SelectTeamMember
              isObjectDataFormat
              id={`${ASSIGNED_USER_ID}${index}`}
              isTeamMembersLoaded={isTeamMembersLoaded(users[fields.value[index][TEAM][TEAM_FIELDS.ID]])}
              name={`${task}.${ASSIGNED_USER_ID}`}
              teamMembers={users[fields.value[index][TEAM][TEAM_FIELDS.ID]]}
              change={change}
            />
          )}
        </Row>
      </Col>
      <Col sm={3}>
        {
          fields.value[index][ASSIGN_TO] && teams.length
            ? (
              <RemoveUserAssignment onClick={removeUserAssignment} />
            )
            : null
        }
      </Col>
    </Row>
  );
};

Assignment.propTypes = {
  fields: PropTypes.shape({
    update: PropTypes.func.isRequired,
    value: PropTypes.array.isRequired,
  }).isRequired,
  getTeams: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  stripes: PropTypes.object.isRequired,
  task: PropTypes.string.isRequired,
  teams: PropTypes.array.isRequired,
  teamsLoaded: PropTypes.bool.isRequired,
  users: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      personal: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string.isRequired,
      }).isRequired,
    })),
  ).isRequired,
};

export default withStripes(Assignment);
